import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { SEOHead } from '@/components/SEOHead';
import { useApp } from '@/context/AppContext';
import { HowItWorks } from '@/components/HowItWorks';
import { SpeedAdvantage } from '@/components/SpeedAdvantage';
import { IndustriesSection } from '@/components/IndustriesSection';
import {
  ArrowRight, Zap, Shield, TrendingUp, Clock, Rocket, Key, Check,
} from 'lucide-react';
import {
  AnimatedBrain, AnimatedRobot, AnimatedCode, AnimatedLightbulb, AnimatedGear,
  AnimatedSparkles, FloatingParticles,
} from '@/components/AnimatedGraphics';

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.2 },
  transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] },
};

export default function LandingPage() {
  const navigate = useNavigate();
  const { plans } = useApp();
  const openFunnel = () => navigate('/landing-page');

  return (
    <main className="bg-background text-foreground min-h-screen">
      <SEOHead
        title="MediaLabs | Build Your Business. We'll Handle the Technology."
        description="Launch, manage and grow your business software with a fully managed development subscription—without the burden of hiring an in-house engineering team."
        path="/"
      />

      {/* ═══════════════════════════════════════
          HERO SECTION
          ═══════════════════════════════════════ */}
      <section className="relative overflow-hidden pt-32 sm:pt-36 lg:pt-44 pb-16 sm:pb-20 lg:pb-28">
        <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(900px 500px at 15% 10%, rgba(30,172,238,0.08), transparent 60%), radial-gradient(700px 420px at 85% 20%, rgba(246,141,44,0.06), transparent 55%)' }} />
        <div className="noise absolute inset-0 pointer-events-none" />
        <FloatingParticles />
        <AnimatedSparkles />

        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-row items-center gap-4 sm:gap-8 lg:gap-16">
            {/* Left — Copy */}
            <div className="w-[62%] sm:w-[58%] lg:w-1/2 shrink-0">
              <motion.p {...fadeUp} className="text-[10px] sm:text-xs font-bold uppercase tracking-[0.2em] text-brand-blue mb-4 sm:mb-6">
                Fully Managed Development Subscription
              </motion.p>

              <motion.h1
                {...fadeUp}
                transition={{ ...fadeUp.transition, delay: 0.1 }}
                className="font-extrabold tracking-tight text-foreground"
                style={{ fontSize: 'clamp(1.4rem, 3.5vw + 0.5rem, 3.5rem)', lineHeight: 1.1 }}
              >
                Build Your Business.{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-brand-blue to-brand-orange">
                  We'll Handle the Technology.
                </span>
              </motion.h1>

              <motion.p
                {...fadeUp}
                transition={{ ...fadeUp.transition, delay: 0.2 }}
                className="mt-4 sm:mt-6 text-muted-foreground leading-relaxed"
                style={{ fontSize: 'clamp(0.72rem, 1.1vw + 0.15rem, 1.05rem)', maxWidth: '500px' }}
              >
                Launch, manage and grow your business software with a fully managed development
                subscription—without the burden of hiring an in-house engineering team.
              </motion.p>

              <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.3 }} className="mt-6 sm:mt-8 flex flex-col sm:flex-row gap-2.5 sm:gap-4">
                <motion.button
                  onClick={openFunnel}
                  whileHover={{ y: -2 }}
                  whileTap={{ scale: 0.98 }}
                  className="pulse-orange inline-flex items-center justify-center gap-2 rounded-xl bg-brand-orange text-white font-bold shadow-lg shadow-brand-orange/25 hover:shadow-xl hover:shadow-brand-orange/30 hover:bg-brand-orange-dark transition-all duration-200"
                  style={{ fontSize: 'clamp(0.72rem, 1vw + 0.15rem, 1rem)', padding: 'clamp(0.6rem, 1vw, 0.875rem) clamp(1rem, 2vw, 1.5rem)' }}
                  data-testid="hero-primary-cta-button"
                >
                  Book a Free Consultation <ArrowRight style={{ width: 'clamp(14px,1.2vw,16px)', height: 'clamp(14px,1.2vw,16px)' }} />
                </motion.button>
                <motion.button
                  onClick={() => navigate('/services')}
                  whileHover={{ y: -1 }}
                  whileTap={{ scale: 0.98 }}
                  className="inline-flex items-center justify-center gap-2 rounded-xl border border-[color:var(--hairline)] bg-white text-foreground font-medium hover:bg-muted transition-colors duration-200"
                  style={{ fontSize: 'clamp(0.72rem, 1vw + 0.15rem, 1rem)', padding: 'clamp(0.6rem, 1vw, 0.875rem) clamp(1rem, 2vw, 1.5rem)' }}
                  data-testid="hero-secondary-cta-button"
                >
                  View Plans &amp; Pricing
                </motion.button>
              </motion.div>

              <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.4 }} className="mt-5 sm:mt-7 flex flex-wrap gap-1.5 sm:gap-2">
                {[{ icon: Shield, text: 'Predictable Monthly Cost' }, { icon: Rocket, text: 'Fully Managed & Maintained' }, { icon: Key, text: 'You Own Your Data' }].map(({ icon: Icon, text }) => (
                  <span key={text} className="inline-flex items-center gap-1 text-muted-foreground bg-secondary/60 border border-[color:var(--hairline)] rounded-full" style={{ fontSize: 'clamp(0.55rem, 0.8vw + 0.1rem, 0.75rem)', padding: 'clamp(0.2rem, 0.4vw, 0.375rem) clamp(0.4rem, 0.8vw, 0.75rem)' }}>
                    <Icon style={{ width: 'clamp(10px,1vw,14px)', height: 'clamp(10px,1vw,14px)' }} className="text-brand-blue" />{text}
                  </span>
                ))}
              </motion.div>
            </div>

            {/* Right — Animated Graphics */}
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7, ease: [0.16,1,0.3,1], delay: 0.4 }} className="w-[38%] sm:w-[42%] lg:w-1/2 shrink-0">
              <div className="relative w-full mx-auto overflow-hidden" style={{ height: 'clamp(220px, 40vw, 520px)' }}>
                <div className="absolute inset-2 sm:inset-4 bg-gradient-to-br from-brand-blue/8 to-brand-orange/6 rounded-2xl sm:rounded-3xl blur-xl sm:blur-2xl" />
                <AnimatedBrain className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-10" size={120} />
                <AnimatedRobot className="absolute top-0 right-0" size={72} />
                <AnimatedCode className="absolute bottom-10 left-0" size={56} />
                <AnimatedLightbulb className="absolute top-0 left-2" size={52} />
                <AnimatedGear className="absolute bottom-6 right-0" size={48} />
                {/* Speed proof card — value-driven metrics */}
                <motion.div className="absolute bottom-0 left-1/2 -translate-x-1/2 bg-white/95 backdrop-blur-sm rounded-lg sm:rounded-xl border border-[color:var(--hairline)] shadow-[var(--shadow-soft)] z-20" style={{ padding: 'clamp(0.4rem, 1vw, 1.25rem)' }} animate={{ y: [0, -3, 0] }} transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}>
                  <div className="flex items-center" style={{ gap: 'clamp(0.4rem, 1vw, 1.25rem)' }}>
                    <div className="text-center"><p className="font-bold text-brand-orange" style={{ fontSize: 'clamp(0.85rem, 1.5vw, 1.5rem)' }}>15</p><p className="text-muted-foreground" style={{ fontSize: 'clamp(0.45rem, 0.6vw, 0.75rem)' }}>Days to V1</p></div>
                    <div className="bg-border" style={{ width: '1px', height: 'clamp(1rem, 2vw, 2.5rem)' }} />
                    <div className="text-center"><p className="font-bold text-brand-blue" style={{ fontSize: 'clamp(0.85rem, 1.5vw, 1.5rem)' }}>CTO</p><p className="text-muted-foreground" style={{ fontSize: 'clamp(0.45rem, 0.6vw, 0.75rem)' }}>Dedicated</p></div>
                    <div className="bg-border" style={{ width: '1px', height: 'clamp(1rem, 2vw, 2.5rem)' }} />
                    <div className="text-center"><p className="font-bold text-foreground" style={{ fontSize: 'clamp(0.85rem, 1.5vw, 1.5rem)' }}>100%</p><p className="text-muted-foreground" style={{ fontSize: 'clamp(0.45rem, 0.6vw, 0.75rem)' }}>Your IP</p></div>
                  </div>
                </motion.div>
                <motion.div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full border border-dashed border-brand-blue/15" style={{ width: 'clamp(160px, 24vw, 420px)', height: 'clamp(160px, 24vw, 420px)' }} animate={{ rotate: 360 }} transition={{ duration: 30, repeat: Infinity, ease: 'linear' }} />
                <motion.div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full border border-dashed border-brand-orange/15" style={{ width: 'clamp(110px, 17vw, 300px)', height: 'clamp(110px, 17vw, 300px)' }} animate={{ rotate: -360 }} transition={{ duration: 25, repeat: Infinity, ease: 'linear' }} />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════
          AGITATION (Animated 2-Column)
          ═══════════════════════════════════════ */}
      <section className="relative py-16 sm:py-20 lg:py-24 border-t border-[color:var(--hairline)] overflow-hidden">
        {/* Background effects */}
        <div className="absolute inset-0 bg-gradient-to-br from-brand-blue/[0.03] via-secondary/30 to-brand-orange/[0.03]" />
        <div className="absolute top-1/3 right-0 w-[400px] h-[400px] bg-brand-orange/[0.05] rounded-full blur-[100px]" />
        <div className="absolute bottom-0 left-1/4 w-[300px] h-[300px] bg-brand-blue/[0.04] rounded-full blur-[80px]" />
        <FloatingParticles />

        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            {/* Left — Copy */}
            <div>
              <motion.h2 {...fadeUp} className="text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-tight text-slate-900 mb-8 sm:mb-10">
                The Traditional Agency Model{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-brand-blue to-brand-orange">is a Trap.</span>
              </motion.h2>
              <div className="flex flex-col gap-6">
                <motion.p initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }}
                  className="text-lg sm:text-xl font-medium text-slate-800 leading-relaxed">
                  You are a CEO. Your sole mandate is business growth and distribution.
                </motion.p>
                <motion.p initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: 0.08 }}
                  className="text-base sm:text-lg text-slate-600 leading-relaxed">
                  Yet, if you rely on traditional development agencies, you spend your time deciphering technical updates, managing scope creep, and negotiating with developers who bill by the hour but deliver by the month. You become a hostage to your own product{'\u2019'}s codebase.
                </motion.p>
                <motion.p initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: 0.16 }}
                  className="text-base sm:text-lg text-slate-600 leading-relaxed">
                  Every week spent wrestling with architecture is a week your competitors spend acquiring your market share.
                </motion.p>
                <motion.p initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: 0.24 }}
                  className="text-lg sm:text-xl font-semibold text-slate-900 leading-relaxed">
                  You do not need a coding vendor. You need an elite engineering partner that operates with absolute certainty and executes at your speed.
                </motion.p>
              </div>
            </div>

            {/* Right — Animated SVG: Tangled Gears → Lightning Breakthrough */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
              className="hidden lg:flex items-center justify-center"
            >
              <div className="relative w-full max-w-[380px] aspect-square">
                {/* Glow behind */}
                <div className="absolute inset-8 bg-gradient-to-br from-brand-blue/10 to-brand-orange/8 rounded-full blur-2xl" />

                <svg viewBox="0 0 400 400" fill="none" className="w-full h-full relative z-10">
                  {/* Outer tangled orbit (slow, represents chaos) */}
                  <motion.circle cx="200" cy="200" r="170" stroke="#1EACEE" strokeWidth="1" strokeDasharray="8 12" opacity="0.2" animate={{ rotate: 360 }} transition={{ duration: 40, repeat: Infinity, ease: 'linear' }} style={{ transformOrigin: '200px 200px' }} />
                  <motion.circle cx="200" cy="200" r="140" stroke="#F68D2C" strokeWidth="1" strokeDasharray="6 10" opacity="0.15" animate={{ rotate: -360 }} transition={{ duration: 30, repeat: Infinity, ease: 'linear' }} style={{ transformOrigin: '200px 200px' }} />

                  {/* Slow gear (top-left, represents old agency model) */}
                  <motion.g animate={{ rotate: 360 }} transition={{ duration: 20, repeat: Infinity, ease: 'linear' }} style={{ transformOrigin: '130px 130px' }}>
                    <path d="M130 100L135 85L140 100L155 95L145 108L160 115L145 120L155 135L140 128L135 143L130 128L115 135L125 120L110 115L125 108L115 95Z" stroke="#94a3b8" strokeWidth="2" fill="rgba(148,163,184,0.06)" />
                    <circle cx="130" cy="115" r="12" stroke="#94a3b8" strokeWidth="1.5" fill="none" />
                  </motion.g>

                  {/* Slow gear (bottom-right) */}
                  <motion.g animate={{ rotate: -360 }} transition={{ duration: 25, repeat: Infinity, ease: 'linear' }} style={{ transformOrigin: '270px 270px' }}>
                    <path d="M270 240L275 225L280 240L295 235L285 248L300 255L285 260L295 275L280 268L275 283L270 268L255 275L265 260L250 255L265 248L255 235Z" stroke="#94a3b8" strokeWidth="2" fill="rgba(148,163,184,0.06)" />
                    <circle cx="270" cy="255" r="12" stroke="#94a3b8" strokeWidth="1.5" fill="none" />
                  </motion.g>

                  {/* Tangled connection lines (messy, chaotic) */}
                  <motion.path d="M145 130 C180 160, 220 140, 255 250" stroke="#94a3b8" strokeWidth="1" fill="none" opacity="0.3" strokeDasharray="4 6" animate={{ pathLength: [0, 1, 0] }} transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }} />
                  <motion.path d="M140 120 C200 200, 160 260, 260 260" stroke="#94a3b8" strokeWidth="1" fill="none" opacity="0.2" strokeDasharray="3 8" animate={{ pathLength: [0, 1, 0] }} transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut', delay: 1 }} />

                  {/* LIGHTNING BOLT (center, breakthrough) */}
                  <motion.path
                    d="M210 80 L185 190 L215 185 L195 320"
                    stroke="#F68D2C"
                    strokeWidth="4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    fill="none"
                    animate={{ pathLength: [0, 1], opacity: [0, 1] }}
                    transition={{ duration: 1.5, repeat: Infinity, repeatDelay: 3, ease: 'easeOut' }}
                  />
                  {/* Lightning glow */}
                  <motion.path
                    d="M210 80 L185 190 L215 185 L195 320"
                    stroke="#F68D2C"
                    strokeWidth="10"
                    strokeLinecap="round"
                    fill="none"
                    opacity="0.15"
                    animate={{ pathLength: [0, 1], opacity: [0, 0.2, 0] }}
                    transition={{ duration: 1.5, repeat: Infinity, repeatDelay: 3, ease: 'easeOut' }}
                  />

                  {/* Fast streamlined orbit (represents MediaLabs speed) */}
                  <motion.circle cx="200" cy="200" r="90" stroke="#1EACEE" strokeWidth="2" fill="none" opacity="0.4" strokeDasharray="40 160" animate={{ rotate: 360 }} transition={{ duration: 4, repeat: Infinity, ease: 'linear' }} style={{ transformOrigin: '200px 200px' }} />

                  {/* Pulsing center node (the solution) */}
                  <motion.circle cx="200" cy="200" r="20" fill="rgba(30,172,238,0.08)" stroke="#1EACEE" strokeWidth="2" animate={{ scale: [1, 1.15, 1], opacity: [0.6, 1, 0.6] }} transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }} />
                  <motion.circle cx="200" cy="200" r="8" fill="#1EACEE" animate={{ scale: [1, 1.2, 1] }} transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }} />

                  {/* Speed particles flying outward */}
                  {[0, 72, 144, 216, 288].map((deg, i) => (
                    <motion.circle
                      key={`particle-${deg}`}
                      cx="200" cy="200" r="3"
                      fill={i % 2 === 0 ? '#1EACEE' : '#F68D2C'}
                      animate={{
                        cx: [200, 200 + Math.cos(deg * Math.PI / 180) * 120],
                        cy: [200, 200 + Math.sin(deg * Math.PI / 180) * 120],
                        opacity: [0.8, 0],
                        r: [3, 1],
                      }}
                      transition={{ duration: 2, repeat: Infinity, delay: i * 0.4, ease: 'easeOut' }}
                    />
                  ))}
                </svg>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ═══ HOW IT WORKS ═══ */}
      <HowItWorks />

      {/* ═══ ENGINEERING AS A GROWTH MULTIPLIER (3 Pillars) ═══ */}
      <section className="py-16 sm:py-20 lg:py-24 border-t border-[color:var(--hairline)]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeUp} className="max-w-3xl mb-12 sm:mb-14">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-foreground mb-3">
              Peace of mind,{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-brand-blue to-brand-orange">built in.</span>
            </h2>
            <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
              We operate as your dedicated technology team on a simple managed subscription—so you can focus on customers and growth, not hiring, standups and technical debt.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
            {[
              { icon: TrendingUp, title: 'Predictable Monthly Cost', body: 'One flat subscription covers development, managed hosting, maintenance and essential security updates. No surprise invoices and no change-order games—just a number you can budget around, with any extra work quoted and approved first.', accent: 'blue' },
              { icon: Shield, title: 'Fully Managed, End to End', body: 'You run the business; we run the technology. Hosting, deployment, maintenance, monitoring and fixes are handled for you. You never have to touch raw code, manage engineers, or chase freelancers again.', accent: 'orange' },
              { icon: Zap, title: 'A Partner for the Long Term', body: 'We do not disappear at launch. Your subscription includes ongoing improvements and scheduled engineering hours, so your software keeps pace with your business as it grows—backed by a team that knows your product.', accent: 'blue' },
            ].map((pillar, i) => {
              const Icon = pillar.icon;
              const isBlue = pillar.accent === 'blue';
              return (
                <motion.div key={pillar.title} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.2 }} transition={{ duration: 0.5, delay: i * 0.1 }} whileHover={{ y: -3 }}
                  className="bg-white rounded-2xl border border-[color:var(--hairline)] p-5 sm:p-6 transition-shadow duration-200 hover:shadow-[var(--shadow-lift)]">
                  <div className={`w-11 h-11 rounded-xl ${isBlue ? 'bg-brand-blue/8' : 'bg-brand-orange/8'} flex items-center justify-center mb-5`}>
                    <Icon className={`w-5 h-5 ${isBlue ? 'text-brand-blue' : 'text-brand-orange'}`} strokeWidth={2} />
                  </div>
                  <h3 className="text-base font-bold text-foreground mb-2">{pillar.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{pillar.body}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ═══ PLANS PREVIEW ═══ */}
      <section className="py-16 sm:py-20 lg:py-24 border-t border-[color:var(--hairline)] bg-slate-50/50" data-testid="home-plans-preview">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeUp} className="text-center max-w-2xl mx-auto mb-10 sm:mb-12">
            <span className="text-xs font-semibold text-brand-blue uppercase tracking-wider">Simple, Managed Plans</span>
            <h2 className="mt-3 text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-foreground">
              One subscription. Your whole technology team.
            </h2>
            <p className="mt-3 text-base text-muted-foreground leading-relaxed">
              Pick the plan that matches your stage today—upgrade as you grow. Every plan is fully managed, with predictable pricing and a {12}-month standard commitment.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
            {(plans || []).map((plan, i) => (
              <motion.div
                key={plan.id}
                initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }} transition={{ duration: 0.5, delay: i * 0.06 }}
                className={`relative flex flex-col rounded-2xl border bg-white p-5 ${plan.highlight ? 'border-brand-blue ring-1 ring-brand-blue/20 shadow-[var(--shadow-lift)]' : 'border-[color:var(--hairline)]'}`}
                data-testid={`home-plan-${plan.id}`}
              >
                {plan.highlight && <span className="absolute -top-2.5 left-5 px-2.5 py-0.5 rounded-full bg-brand-blue text-white text-[10px] font-bold">Most popular</span>}
                <h3 className="text-base font-bold text-foreground">{plan.name}</h3>
                <div className="mt-2 mb-1">
                  {plan.quote_only ? (
                    <p className="text-2xl font-bold text-foreground">Custom</p>
                  ) : (
                    <p className="text-2xl font-bold text-foreground">${Number(plan.monthly_usd).toLocaleString()}<span className="text-sm font-medium text-muted-foreground">/mo</span></p>
                  )}
                </div>
                <p className="text-xs text-muted-foreground leading-relaxed min-h-[48px]">{plan.tagline}</p>
                <Link to={`/services`} className={`mt-4 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold transition-colors duration-200 ${plan.highlight ? 'bg-brand-blue text-white hover:bg-brand-blue-dark' : 'bg-foreground text-white hover:opacity-90'}`} data-testid={`home-plan-cta-${plan.id}`}>
                  {plan.quote_only ? 'Request a quote' : 'See details'} <ArrowRight className="w-3.5 h-3.5" />
                </Link>
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-8">
            <Link to="/services" className="inline-flex items-center gap-2 text-sm font-bold text-brand-blue hover:text-brand-blue-dark transition-colors duration-200" data-testid="home-view-all-plans">
              Compare all plans &amp; features <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* ═══ SPEED ADVANTAGE ═══ */}
      <SpeedAdvantage />

      {/* ═══ INDUSTRIES ═══ */}
      <IndustriesSection />

      {/* ═══════════════════════════════════════
          FINAL CTA
          ═══════════════════════════════════════ */}
      <section className="py-16 sm:py-20 lg:py-28 border-t border-[color:var(--hairline)]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div {...fadeUp}>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-foreground mb-4">
              Hand off the technology.{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-brand-blue to-brand-orange">Grow the business.</span>
            </h2>
            <p className="text-base sm:text-lg text-muted-foreground leading-relaxed max-w-xl mx-auto mb-8">
              Book a short, no-pressure consultation. We'll talk through your goals, scope a sensible first version, and give you a clear plan with a predictable monthly price.
            </p>
            <motion.button
              onClick={openFunnel}
              whileHover={{ y: -2 }}
              whileTap={{ scale: 0.98 }}
              className="pulse-orange inline-flex items-center gap-2.5 px-8 py-4 rounded-xl bg-brand-orange text-white text-base font-bold shadow-lg shadow-brand-orange/25 hover:shadow-xl hover:shadow-brand-orange/30 hover:bg-brand-orange-dark transition-all duration-200"
              data-testid="final-cta-button"
            >
              Book a Free Consultation <ArrowRight className="w-4 h-4" />
            </motion.button>
          </motion.div>
        </div>
      </section>
    </main>
  );
}
